/* eslint-disable */
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
} from 'react';

const useMediaQuery = mediaQuery => {
  const [matches, setMatches] = useState(
    () => window.matchMedia(mediaQuery).matches,
  );
  useLayoutEffect(() => {
    const mediaQueryList = window.matchMedia(mediaQuery);
    const listener = e => setMatches(e.matches);
    mediaQueryList.addListener(listener);
    return () => mediaQueryList.removeListener(listener);
  }, [mediaQuery]);

  return matches;
};

const useRect = config => {
  const { element } = config;

  const [dimensions, set_dimensions] = useState(false);

  useEffect(() => {
    if (!element.current) {
      return;
    }
    on_resize();
    if (typeof ResizeObserver === 'function') {
      let observer = new ResizeObserver(() => on_resize());
      observer.observe(element.current);

      return () => {
        if (!observer) {
          return;
        }

        observer.disconnect();
        observer = null;
      };
    }
  }, [element.current]);

  const on_resize = useCallback(() => {
    if (!element.current) {
      return;
    }
    const rect = element.current.getBoundingClientRect();
    if (rect) {
      set_dimensions({
        height: rect.height,
        width: rect.width,
      });
    }
  }, [element.current]);

  return {
    status: dimensions ? 'ready' : 'loading',
    dimensions,
  };
};

const useClickOutside = callback => {
  const container = useRef(null);
  const [isTouchEvent, setTouchEvent] = useState(false);
  const eventType = isTouchEvent ? 'touchend' : 'click';

  function handleEvent(e) {
    if (e.type === 'click' && isTouchEvent) { return; } // prettier-ignore

    if (container.current && e.target !== null) {
      if (!container.current.contains(e.target)) {
        callback(e);
      }
    }
  }

  useEffect(() => {
    document.addEventListener(eventType, handleEvent, true);

    return () => {
      document.removeEventListener(eventType, handleEvent, true);
    };
  });

  useEffect(() => {
    setTouchEvent('ontouchstart' in document.documentElement);
  }, []);

  return container;
};

const hooks = {
  useClickOutside,
  useRect,
  useMediaQuery,
};

// Promise.resolve is good for wrapping synchronous code
// Promise.resolve().then(function () {
//   if (somethingIsNotRight()) {
//     throw new Error("I will be rejected asynchronously!");
//   } else {
//     return "This string will be resolved asynchronously!";
//   }
// });

const isLast = (arr, i) => i === arr.length - 1;
const makeId = _ =>
  Math.random()
    .toString(36)
    .substring(2, 15);
const noop = () => {};
const callAll = (...fns) => (...args) => fns.forEach(fn => fn && fn(...args));
const unwrapArr = arg => (Array.isArray(arg) ? arg[0] : arg);
const roundTo = (n, precision) =>
  Math.round(n * 10 ** precision) / 10 ** precision;
const range = (start, stop, step) => {
  var a = [start],
    b = start;
  while (b < stop) {
    a.push((b += step || 1));
  }
  return a;
};
const isArrxy = x => Array.isArray(x);

const snap = points => {
  if (typeof points === 'number') {
    return v => Math.round(v / points) * points;
  } else {
    let i = 0;
    const numPoints = points.length;

    return v => {
      let lastDistance = Math.abs(points[0] - v);

      for (i = 1; i < numPoints; i++) {
        const point = points[i];
        const distance = Math.abs(point - v);

        if (distance === 0) return point;

        if (distance > lastDistance) return points[i - 1];

        if (i === numPoints - 1) return point;

        lastDistance = distance;
      }
    };
  }
};
function isEmptyObject(obj) {
  return Object.keys(obj).length === 0;
}
function isEquivalent(a, b) {
  // Create arrays of property names
  var aProps = Object.getOwnPropertyNames(a);
  var bProps = Object.getOwnPropertyNames(b);

  // If number of properties is different,
  // objects are not equivalent
  if (aProps.length != bProps.length) {
    return false;
  }

  for (var i = 0; i < aProps.length; i++) {
    var propName = aProps[i];

    // If values of same property are not equal,
    // objects are not equivalent
    if (a[propName] !== b[propName]) {
      return false;
    }
  }

  // If we made it this far, objects
  // are considered equivalent
  return true;
}
const utils = {
  callAll,
  isArrxy,
  isEmptyObject,
  isEquivalent,
  isLast,
  makeId,
  noop,
  range,
  roundTo,
  snap,
  unwrapArr,
};

export default {
  ...hooks,
  ...utils,
};
